import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChartPie, ShoppingCart, CurrencyDollar, TrendUp, Package } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export function POSDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/pos/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => toast.error("Error al cargar dashboard"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  const methodLabels = { efectivo: "Efectivo", tarjeta_debito: "T. Débito", tarjeta_credito: "T. Crédito", t_credito: "T. Crédito", t_debito: "T. Débito" };
  const methodData = Object.entries(data.today_by_method || {}).map(([k, v]) => ({
    name: methodLabels[k] || k,
    total: v,
  }));

  return (
    <div className="space-y-6" data-testid="pos-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ChartPie size={24} weight="duotone" className="text-blue-600" /> Dashboard POS</h1>
        <p className="text-sm text-slate-500">Resumen de operaciones del punto de venta</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={ShoppingCart} label="Ventas Hoy" value={data.today_sales_count} color="blue" />
        <KPI icon={CurrencyDollar} label="Total Hoy" value={`$${data.today_total.toLocaleString("es-MX")}`} color="emerald" />
        <KPI icon={TrendUp} label="Ventas Mes" value={data.month_sales_count} color="violet" />
        <KPI icon={CurrencyDollar} label="Total Mes" value={`$${data.month_total.toLocaleString("es-MX")}`} color="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Payment method breakdown */}
        <div className="bg-white border rounded-xl p-5">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-4">Ventas Hoy por Metodo de Pago</h3>
          {methodData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={methodData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `$${v.toLocaleString("es-MX")}`} />
                <Bar dataKey="total" fill="#2563EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-10">Sin ventas hoy</p>}
        </div>

        {/* Top products */}
        <div className="bg-white border rounded-xl p-5">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-4">Productos Mas Vendidos (Mes)</h3>
          {data.top_products?.length > 0 ? (
            <div className="space-y-2">
              {data.top_products.slice(0, 8).map((p, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 text-xs border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="font-semibold text-slate-800 truncate max-w-[200px]">{p.name}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-slate-400">{p.qty} uds</span>
                    <span className="font-bold text-slate-900 font-mono">${p.total.toLocaleString("es-MX")}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-10">Sin datos</p>}
        </div>
      </div>

      {/* Cash register status */}
      <div className={`p-4 rounded-xl border ${data.cash_register_open ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
        <p className="text-sm font-bold text-slate-800">Estado de Caja: <span className={data.cash_register_open ? "text-emerald-600" : "text-amber-600"}>{data.cash_register_open ? "Abierta" : "Cerrada"}</span></p>
        {data.cash_register && <p className="text-xs text-slate-500">Apertura: ${data.cash_register.opening_amount?.toLocaleString("es-MX")} · {data.cash_register.opened_by_name}</p>}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  const colors = { blue: "bg-blue-50 text-blue-600 border-blue-200", emerald: "bg-emerald-50 text-emerald-600 border-emerald-200", violet: "bg-violet-50 text-violet-600 border-violet-200", amber: "bg-amber-50 text-amber-600 border-amber-200" };
  return (
    <div className={`border rounded-xl p-4 ${colors[color]}`}>
      <Icon size={20} weight="duotone" className="mb-2" />
      <p className="text-2xl font-extrabold">{value}</p>
      <p className="text-[10px] font-semibold uppercase mt-1 opacity-70">{label}</p>
    </div>
  );
}
